'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from 'react'

const ACTIVE_KEY = 'focus-mode-active'

const PROXIMITY_PX = 60
const THROTTLE_MS = 50
const TOUCH_REVEAL_MS = 2000

// In-memory fallback for sandboxed iframes where localStorage is blocked.
const memoryStore = new Map<string, string>()

function safeGetItem(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return memoryStore.get(key) ?? null
  }
}

function safeSetItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value)
  } catch {
    memoryStore.set(key, value)
  }
}

interface FocusModeContextValue {
  focusMode: boolean
  isShortViewport: boolean
  toggle: () => void
}

const FocusModeContext = createContext<FocusModeContextValue>({
  focusMode: false,
  isShortViewport: false,
  toggle: () => {},
})

export function useFocusMode() {
  return useContext(FocusModeContext)
}

export default function FocusModeProvider({ children }: { children: React.ReactNode }) {
  const [focusMode, setFocusMode] = useState(false)
  const [isShortViewport, setIsShortViewport] = useState(false)
  const lastTickRef = useRef(0)
  const touchHeaderTimeoutRef = useRef<number | null>(null)

  // Initialize: restore persisted state only (no auto-activation).
  useEffect(() => {
    const persisted = safeGetItem(ACTIVE_KEY)
    const short = window.innerHeight <= 900
    setIsShortViewport(short)

    if (persisted === '1') {
      setFocusMode(true)
    }
  }, [])

  // Sync data attribute on <html>
  useEffect(() => {
    const doc = document.documentElement
    if (focusMode) {
      doc.setAttribute('data-focus-mode', '')
      return
    }

    doc.removeAttribute('data-focus-mode')
    doc.removeAttribute('data-focus-header')
  }, [focusMode])

  // Mouse proximity + touch edge-tap reveal for header/footer in focus mode.
  useEffect(() => {
    if (!focusMode) return

    const doc = document.documentElement

    function clearTimer(ref: MutableRefObject<number | null>) {
      if (ref.current == null) return
      window.clearTimeout(ref.current)
      ref.current = null
    }

    function revealHeaderTemporarily() {
      doc.setAttribute('data-focus-header', '')
      clearTimer(touchHeaderTimeoutRef)
      touchHeaderTimeoutRef.current = window.setTimeout(() => {
        doc.removeAttribute('data-focus-header')
        touchHeaderTimeoutRef.current = null
      }, TOUCH_REVEAL_MS)
    }

    function onPointerMove(e: PointerEvent) {
      // Only proximity-reveal for mouse/trackpad to avoid work during touch scroll.
      if (e.pointerType && e.pointerType !== 'mouse') return

      const now = Date.now()
      if (now - lastTickRef.current < THROTTLE_MS) return
      lastTickRef.current = now

      if (e.clientY < PROXIMITY_PX) {
        doc.setAttribute('data-focus-header', '')
      } else {
        doc.removeAttribute('data-focus-header')
      }
    }

    function onPointerDown(e: PointerEvent) {
      // On touch devices, edge-tap reveals header/footer for a short time.
      if (e.pointerType !== 'touch') return

      if (e.clientY < PROXIMITY_PX) {
        revealHeaderTemporarily()
      }
    }

    function onMouseLeave() {
      doc.removeAttribute('data-focus-header')
      clearTimer(touchHeaderTimeoutRef)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerdown', onPointerDown, { passive: true })
    doc.addEventListener('mouseleave', onMouseLeave)
    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerdown', onPointerDown)
      doc.removeEventListener('mouseleave', onMouseLeave)
      doc.removeAttribute('data-focus-header')
      clearTimer(touchHeaderTimeoutRef)
    }
  }, [focusMode])

  // Escape key toggles focus mode.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return

      setFocusMode((prev) => {
        const next = !prev
        safeSetItem(ACTIVE_KEY, next ? '1' : '0')

        return next
      })
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const toggle = useCallback(() => {
    setFocusMode((prev) => {
      const next = !prev
      safeSetItem(ACTIVE_KEY, next ? '1' : '0')

      return next
    })
  }, [])

  return (
    <FocusModeContext.Provider value={{ focusMode, isShortViewport, toggle }}>
      {children}
    </FocusModeContext.Provider>
  )
}
